import { dbFetchMaps, dbFetchAllMaps, dbFindDuplicateMap } from '@/api/maps';
import { MapData } from './types/MapData';
import { SupabaseClient } from '@supabase/supabase-js';
import { logger } from '@/lib/logger';

/**
 * Fetch paginated maps
 */
export const fetchMaps = async ({
    pageParam = 0,
    limit = 20,
    userId
}: {
    pageParam?: number;
    limit?: number;
    userId: string;
} = { userId: '' }, client?: SupabaseClient): Promise<{ data: MapData[]; nextCursor: number | null }> => {
    const from = pageParam * limit;
    const to = from + limit - 1;

    try {
        const { data, count } = await dbFetchMaps(userId, from, to, client!);
        const nextCursor = (count && to < count - 1) ? pageParam + 1 : null;
        return { data, nextCursor };
    } catch (error: unknown) {
        logger.error('Error fetching maps', error);
        throw error;
    }
};

/**
 * Fetch all maps with legacy data validation
 */
export const fetchAllMaps = async (userId: string, client?: SupabaseClient): Promise<MapData[]> => {
    try {
        // userId is mandatory now
        return await dbFetchAllMaps(userId, client!);
    } catch (error: unknown) {
        logger.error('Error fetching all maps', error, { operation: 'fetch_all_maps', table: 'maps' });
        throw error;
    }
}

/**
 * Check for existing map with same identifiers
 */
export const checkForDuplicateMap = async (
    userId: string,
    firstName: string,
    familyName: string | null,
    motherName: string | null,
    birthDate: string | null,
    client?: SupabaseClient
): Promise<string | null> => {
    try {
        return await dbFindDuplicateMap({
            userId,
            firstName,
            familyName,
            motherName,
            birthDate
        }, client);
    } catch (error: unknown) {
        logger.error('Error checking for duplicate map', error, { operation: 'check_duplicate_map', table: 'maps' });
        // We return null on error to fail safe (allow creation vs blocking user), 
        // but log it. Or we could throw. Returning null is safer for UX.
        return null;
    }
};
